/**
 * Which GST a document should show, and at what rate.
 *
 * THE RULE. Indian GST is charged one of two ways depending on whether the SUPPLIER's state and
 * the place of supply are the same. Same state is intrastate: CGST and SGST, each at half the
 * rate. Different states is interstate: IGST at the whole rate. There is no third case.
 *
 * SUPPLIER, NOT ISSUER. On a production order the brand issues the document but the manufacturer
 * is the supplier, so the comparison is the MANUFACTURER's state against where the goods go. The
 * brand's own state does not enter it — a Haryana brand buying from a Gujarat manufacturer for
 * delivery to Haryana is still interstate, because the goods cross a border on their way.
 *
 * THE SUPPLIER'S STATE IS INSIDE ITS GSTIN. A GSTIN is constructed with its state code as the
 * first two characters, so '24AADFW9354L1ZN' is Gujarat. Nothing needs to store it separately.
 *
 * SHARED BY THE PDF AND THE SCREEN, deliberately. The preview exists to show what the
 * manufacturer will receive, so the two must reach the same answer from the same inputs — a
 * second copy of this rule is a second chance to get a tax wrong.
 */
/** The state a GSTIN belongs to, or null when there isn't one to read. */
export function stateCodeFromGstin(gstin) {
    const trimmed = gstin?.trim();
    if (!trimmed || trimmed.length < 2)
        return null;
    const prefix = trimmed.slice(0, 2);
    return /^[0-9]{2}$/.test(prefix) ? prefix : null;
}
/**
 * Whether a document should charge IGST or CGST+SGST.
 *
 * UNKNOWN IS A REAL ANSWER and not a failure. An unregistered manufacturer has no GSTIN, and an
 * order raised before place of supply was recorded has no destination state — in both cases the
 * honest thing is to print a single "Tax" line rather than guess a split, because guessing wrong
 * puts the wrong tax on a document somebody files.
 */
export function gstTreatment(supplierGstin, placeOfSupplyStateCode) {
    const supplierState = stateCodeFromGstin(supplierGstin);
    const destination = placeOfSupplyStateCode?.trim();
    if (!supplierState || !destination)
        return 'UNKNOWN';
    return supplierState === destination ? 'INTRASTATE' : 'INTERSTATE';
}
/**
 * One line's or one order's tax, split the way the document must present it.
 *
 * THE HALVES ARE COMPUTED FROM THE AMOUNT, not from the rate, so they always add back to it.
 * Halving the rate and re-multiplying would round twice and can leave CGST + SGST a paisa short
 * of the tax reported above it — on a document whose whole job is to be added up.
 */
export function splitGst(taxAmount, rate, treatment) {
    const tax = Number(taxAmount) || 0;
    const fullRate = Number(rate) || 0;
    if (treatment === 'INTERSTATE') {
        return { treatment, igst: tax, igstRate: fullRate };
    }
    if (treatment === 'INTRASTATE') {
        // Round the first half and take the remainder as the second, so the two always sum to
        // the tax exactly even when it is an odd number of paise.
        const cgst = Math.round((tax / 2) * 100) / 100;
        return { treatment, cgst, sgst: Math.round((tax - cgst) * 100) / 100, halfRate: fullRate / 2 };
    }
    return { treatment: 'UNKNOWN', tax };
}
/**
 * Lines rolled up for the per-HSN tax summary every Indian tax document carries.
 *
 * GROUPED BY HSN AND RATE TOGETHER, not by HSN alone: the same code can appear on two lines at
 * different slabs when one is exempt and another is not, and collapsing those would report a
 * rate that applies to neither.
 *
 * LINES WITH NO STATED RATE ARE LEFT OUT rather than summarised at zero — a summary row saying
 * 0% is a claim of exemption, and nobody made it.
 *
 * Shared by the PDF and the screen for the same reason the split above is: the preview exists to
 * show what the manufacturer will receive, so it must group the same way or it is showing
 * something else.
 */
export function groupTaxByHsn(lines) {
    const groups = new Map();
    for (const line of lines) {
        /*
         * Stated, and stated as a number. `Number(null)` and `Number('')` are both 0, so the
         * emptiness checks cannot be folded into the finite one — and a rate that is not a
         * finite number is not a rate, however readily it coerces.
         */
        const given = line.gstRate !== null && line.gstRate !== undefined && line.gstRate !== '';
        const rate = Number(line.gstRate);
        if (given && Number.isFinite(rate)) {
            const hsn = line.hsnCode?.trim() || '—';
            const taxable = (Number(line.quantity) || 0) * (Number(line.unitPrice) || 0);
            const key = `${hsn}::${rate}`;
            const group = groups.get(key) ?? { hsn, rate, taxable: 0, tax: 0 };
            group.taxable += taxable;
            group.tax += (taxable * rate) / 100;
            groups.set(key, group);
        }
    }
    return [...groups.values()];
}
//# sourceMappingURL=gstSplit.js.map